// Globals
import { Navigate, Outlet, useLocation } from 'react-router-dom';

// Store
import { useAppState } from '@/store';

// Models
import { ROUTES } from '@/models/router.model';

// Components
import { Spinner } from '@/ui';

// Modules
import { selectIsAuthorized, useUnauthorizedRedirect } from '@/modules/Auth';
import { selectAdminProfile, selectAdminProfileError } from '@/modules/AdminProfile';
import { useRoutePermissions } from '@/modules/Permissions';

const ProtectedRoute = ({ redirectPath = ROUTES.SIGN_IN }: { redirectPath?: ROUTES }) => {
  const isAuthenticated = useAppState(selectIsAuthorized);
  const profile = useAppState(selectAdminProfile);
  const profileError = useAppState(selectAdminProfileError);
  const location = useLocation();
  const isRouteAllowed = useRoutePermissions(location.pathname as ROUTES);
  useUnauthorizedRedirect({});

  if (!isAuthenticated && isAuthenticated !== null) {
    return <Navigate to={redirectPath} replace />;
  }

  // Authenticated (or still resolving the session) but the profile — and with it the
  // permission set — has not loaded yet. Do NOT mount the target page here: an early
  // mount fires permission-restricted requests whose 403 the http client turns into a
  // forced sign-out instead of the expected redirect to /profile. Show a loader until
  // the profile resolves, then evaluate the route permission below.
  if (!profile) {
    // Profile fetch failed with a non-auth error (e.g. 5xx / network). A 401 would have
    // already flipped isAuthorized and been handled above, so reaching here means we
    // cannot evaluate permissions — fall back to sign-in instead of hanging on the loader.
    if (profileError) {
      return <Navigate to={redirectPath} replace />;
    }

    return <Spinner />;
  }

  if (!isRouteAllowed) {
    return <Navigate to={ROUTES.PROFILE} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
